// v2.13.0 — AddressingMapMapbox : Mapbox Standard 3D pour la page /addressing.
// Différences vs DeliveryMapMapbox (v2.4.0) :
//   - pitch initial 45° (config-mode, pas full nav 60°)
//   - marker USER draggable (le user peut affiner sa position avant génération)
//   - marker commerces multiples (cliquables → popup HTML custom)
//   - flyTo controllable par prop
//
// Fallback Leaflet géré côté parent (AddressingPage) si token invalide.

import { useEffect, useRef } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

const STYLE = "mapbox://styles/mapbox/standard";

export default function AddressingMapMapbox({
  token,
  abidjanCenter,
  userPos,             // [lat, lng] | null — pin draggable
  onUserPosChange,     // (lat, lng) → void — appelé après drag end
  commerces,           // GeoJSON FeatureCollection
  flyTo,               // [lat, lng] | null
  onCommerceOrder,     // (feature) => void
  onTokenInvalid,      // () => void
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const userMarkerRef = useRef(null);
  const commerceMarkersRef = useRef([]);
  const tokenInvalidFiredRef = useRef(false);

  // Init Mapbox.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    mapboxgl.accessToken = token;

    let map;
    try {
      map = new mapboxgl.Map({
        container: containerRef.current,
        style: STYLE,
        center: [abidjanCenter[1], abidjanCenter[0]], // [lng, lat]
        zoom: 12,
        pitch: 45,
        bearing: 0,
        antialias: true,
        attributionControl: false,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[addressing-mapbox-init-error]", err && err.message);
      const m = (err && err.message) || "";
      if (/webgl|context/i.test(m) && !tokenInvalidFiredRef.current) {
        tokenInvalidFiredRef.current = true;
        onTokenInvalid && onTokenInvalid("no_webgl");
      }
      return;
    }

    mapRef.current = map;

    map.on("error", (e) => {
      const status = e?.error?.status;
      if ((status === 401 || status === 403) && !tokenInvalidFiredRef.current) {
        tokenInvalidFiredRef.current = true;
        onTokenInvalid && onTokenInvalid("auth_error");
      }
    });

    map.addControl(new mapboxgl.NavigationControl({ visualizePitch: true }), "top-right");

    return () => {
      try { map.remove(); } catch (_) {}
      mapRef.current = null;
      userMarkerRef.current = null;
      commerceMarkersRef.current = [];
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Update USER marker (draggable) when userPos changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!userPos || !Number.isFinite(userPos[0]) || !Number.isFinite(userPos[1])) {
      if (userMarkerRef.current) { userMarkerRef.current.remove(); userMarkerRef.current = null; }
      return;
    }
    const [lat, lng] = userPos;
    if (userMarkerRef.current) {
      userMarkerRef.current.setLngLat([lng, lat]);
    } else {
      const marker = new mapboxgl.Marker({ color: "#DC2626", draggable: true })
        .setLngLat([lng, lat])
        .addTo(map);
      marker.on("dragend", () => {
        const ll = marker.getLngLat();
        onUserPosChange && onUserPosChange(ll.lat, ll.lng);
      });
      userMarkerRef.current = marker;
    }
  }, [userPos, onUserPosChange]);

  // FlyTo.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !flyTo) return;
    if (!Number.isFinite(flyTo[0]) || !Number.isFinite(flyTo[1])) return;
    map.flyTo({ center: [flyTo[1], flyTo[0]], zoom: 17, pitch: 45, duration: 1200 });
  }, [flyTo]);

  // Commerce markers re-render on change.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    // Clear existing
    commerceMarkersRef.current.forEach((m) => { try { m.remove(); } catch (_) {} });
    commerceMarkersRef.current = [];
    const feats = (commerces && commerces.features) || [];
    for (const f of feats) {
      const [lng, lat] = f.geometry?.coordinates || [];
      if (typeof lat !== "number" || typeof lng !== "number") continue;
      const fid = f.properties.id_etablissement || f.properties.id;
      const popupHtml = `
        <div style="font-family:system-ui,-apple-system,sans-serif;font-size:13px;min-width:160px">
          <strong>${escapeHtml(f.properties.nom || "")}</strong><br/>
          <span style="opacity:.7">${escapeHtml(f.properties.categorie || "")}</span><br/>
          <code style="font-size:11px">${escapeHtml(f.properties.adresse_virtuelle || "")}</code><br/>
          <button data-commerce-order="${escapeHtml(String(fid))}" style="margin-top:6px;padding:4px 10px;cursor:pointer;background:#1e293b;color:#fff;border:none;border-radius:4px;font-size:12px">🛒 Commander</button>
        </div>`;
      const popup = new mapboxgl.Popup({ offset: 18, closeButton: true, maxWidth: "240px" })
        .setHTML(popupHtml);
      popup.on("open", () => {
        const btn = popup.getElement().querySelector(`[data-commerce-order="${fid}"]`);
        if (btn) btn.addEventListener("click", () => onCommerceOrder && onCommerceOrder(f), { once: true });
      });
      const m = new mapboxgl.Marker({ color: "#3B82F6" })
        .setLngLat([lng, lat])
        .setPopup(popup)
        .addTo(map);
      commerceMarkersRef.current.push(m);
    }
  }, [commerces, onCommerceOrder]);

  return (
    <div
      ref={containerRef}
      data-testid="addressing-map-mapbox"
      data-engine="mapbox"
      style={{ width: "100%", height: "100%" }}
    />
  );
}

function escapeHtml(str) {
  return String(str).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}
